/**
 * 입력 모달 dirty 확인 훅.
 *
 * 사용 패턴:
 *   const { markDirty, requestClose } = useDirtyClose(onClose);
 *
 *   - 모달 컨테이너 div 또는 form 에 onInput={markDirty} onChange={markDirty} 부착
 *     (React 이벤트는 children 의 input/change 가 부모로 bubble — 한 군데 한 번이면 충분)
 *   - [×] 버튼 / [취소] 버튼 / 백드롭 (사용 시) 의 onClick 을 requestClose 로 교체
 *   - body overflow 잠금은 본 훅이 자동 처리.
 *   - ⭐ESC 로는 **닫지 않는다** (v2.231.0 · 사용자 결정 H-4 「입력하는 창은 ESC 로 안 닫힘」).
 *     전에는 이 훅이 ESC 를 받아 아무것도 안 적었으면 바로 닫고, 「저장할까요?」에서 [취소]를 누르면 저장 없이 닫았다.
 *     대신 창 쌓임(lib/dialog-stack.ts)에 입력 창으로 올라가 ESC 를 삼킨다 — 아래 보기 창이 ESC 로 닫히지 않게.
 *
 * 동작:
 *   - 한 번이라도 입력 이벤트가 발생했으면 dirty=true 로 표시
 *   - requestClose 호출 시 dirty 면 confirm 다이얼로그. 사용자가 [취소] 하면 닫기 보류.
 *   - 저장 성공 → onSaved 경로는 본 훅 우회 (onClose 직접 호출) — 정상 닫힘.
 *
 * 저장 확인 모드 (opts.onSaveAndClose 전달 시):
 *   - dirty 면 "변경된 내용을 저장하시겠습니까?" 로 물음.
 *     [확인] → onSaveAndClose() 호출 (검증·저장은 모달 측 책임, 성공 시 모달이 닫힘).
 *     [취소] → 저장하지 않고 그대로 닫음.
 *   - 변동 없으면 그냥 닫음.
 *
 * 의도된 한계:
 *   - 입력했다가 원래 값으로 되돌려도 dirty 유지 (over-prompt 가능). 단순함 우선.
 *   - 모달이 별도로 window ESC 귀를 두면 창 쌓임을 건너뛴다 — 두지 말 것(check:modal ③).
 */

import { useCallback, useEffect, useRef } from 'react';
import { useDialogLayer } from './dialog-stack';

const CONFIRM_MSG =
  '입력 또는 수정을 취소하시겠습니까?\n변경한 내용은 저장되지 않습니다.';

const SAVE_MSG =
  '변경된 내용이 있습니다. 저장하시겠습니까?\n\n[확인] 저장 후 닫기\n[취소] 저장하지 않고 닫기';

interface DirtyCloseOpts {
  /**
   * 전달 시 "저장 확인" 모드. dirty 상태로 닫으려 하면 저장 여부를 묻고,
   * [확인] 시 이 콜백을 호출(검증·저장·닫기는 콜백/모달 책임). [취소] 시 저장 없이 닫음.
   */
  onSaveAndClose?: () => void;
}

export function useDirtyClose(onClose: () => void, opts: DirtyCloseOpts = {}) {
  const dirtyRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const onSaveRef = useRef(opts.onSaveAndClose);
  // 최신 onClose 보존 — props 변경에도 ESC 핸들러가 stale 안 되도록
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    onSaveRef.current = opts.onSaveAndClose;
  }, [opts.onSaveAndClose]);

  const markDirty = useCallback(() => {
    dirtyRef.current = true;
  }, []);

  const resetDirty = useCallback(() => {
    dirtyRef.current = false;
  }, []);

  const requestClose = useCallback(() => {
    if (dirtyRef.current) {
      // 저장 확인 모드 — 저장 여부를 물음
      if (onSaveRef.current) {
        if (window.confirm(SAVE_MSG)) {
          onSaveRef.current();  // 저장(성공 시 모달이 onSaved/onClose 로 닫힘)
          return;
        }
        // [취소] = 저장하지 않고 닫기
        onCloseRef.current();
        return;
      }
      // 기존 동작 — 변경 내용 버림 확인
      if (!window.confirm(CONFIRM_MSG)) return;
    }
    onCloseRef.current();
  }, []);

  // 입력 창 — ESC 로 닫지 않는다(받아서 삼킨다) (v2.231.0 · H-4)
  useDialogLayer(true, null);

  // body overflow 잠금
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return { markDirty, resetDirty, requestClose };
}
